import React, { useState, useEffect } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  getCommunicationSettingsAPI,
  updateCommunicationSettingsAPI,
} from '@/api/room'
import { toast } from 'sonner'
import { InfoIcon } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface CommunicationSettingsFormProps {
  roomId: string
}

const PLACEHOLDERS = [
  { key: '{player_name}', description: "Player's first name" },
  { key: '{room_name}', description: 'Game room name' },
  { key: '{invite_code}', description: 'Invitation code for joining' },
  { key: '{professor_name}', description: "Professor's full name" },
  { key: '{professor_email}', description: "Professor's email address" },
  { key: '{game_server_url}', description: 'Frontend game server URL' },
  {
    key: '{reset_password_url}',
    description: 'Password reset URL (only shown if player needs to set password)',
  },
]

const DEFAULT_TEMPLATE = `Hello {player_name},

You have been invited to join the game room '{room_name}'.

Invite Code: {invite_code}
Game Server: {game_server_url}

Please join at your earliest convenience.

If you need any assistance or have questions, please contact:
Professor {professor_name} at {professor_email}

Best regards,
Professor {professor_name}`

export const CommunicationSettingsForm: React.FC<
  CommunicationSettingsFormProps
> = ({ roomId }) => {
  const [template, setTemplate] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [preview, setPreview] = useState<string>('')

  useEffect(() => {
    loadSettings()
  }, [roomId])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const data = await getCommunicationSettingsAPI(roomId)
      const templateValue =
        data.invitation_message_template || DEFAULT_TEMPLATE
      setTemplate(templateValue)
      updatePreview(templateValue)
    } catch (error) {
      console.error('Failed to load communication settings:', error)
      toast.error('Failed to load communication settings')
      setTemplate(DEFAULT_TEMPLATE)
      updatePreview(DEFAULT_TEMPLATE)
    } finally {
      setLoading(false)
    }
  }

  const updatePreview = (templateText: string) => {
    const sampleData = {
      player_name: 'John',
      room_name: 'Sample Game Room',
      invite_code: 'ABC123XYZ',
      professor_name: 'Dr. Jane Smith',
      professor_email: 'jane.smith@example.com',
      game_server_url: 'https://game.example.com',
      reset_password_url: 'https://game.example.com/reset-password/?uid=123&token=abc',
    }

    let previewText = templateText
    for (const [key, value] of Object.entries(sampleData)) {
      previewText = previewText.replace(
        new RegExp(`\\{${key}\\}`, 'g'),
        value
      )
    }
    setPreview(previewText)
  }

  const handleTemplateChange = (value: string) => {
    setTemplate(value)
    updatePreview(value)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await updateCommunicationSettingsAPI(roomId, template)
      toast.success('Communication settings saved successfully')
    } catch (error) {
      console.error('Failed to save communication settings:', error)
      toast.error('Failed to save communication settings')
    } finally {
      setSaving(false)
    }
  }

  const handleInsertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById('invitation-template') as HTMLTextAreaElement
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = template
      const newText = text.substring(0, start) + placeholder + text.substring(end)
      handleTemplateChange(newText)
      // Set cursor position after inserted placeholder
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length)
      }, 0)
    }
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-gray-500'>Loading communication settings...</p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <Label htmlFor='invitation-template' className='text-base font-semibold'>
            Invitation Message Template
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className='h-4 w-4 text-gray-400 cursor-help' />
              </TooltipTrigger>
              <TooltipContent className='max-w-xs'>
                <p>
                  Use placeholders like {'{player_name}'}, {'{room_name}'}, etc.
                  These will be replaced with actual values when sending invitations.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className='space-y-2'>
          <Textarea
            id='invitation-template'
            value={template}
            onChange={(e) => handleTemplateChange(e.target.value)}
            placeholder={DEFAULT_TEMPLATE}
            className='min-h-[300px] font-mono text-sm'
            rows={15}
          />
          <p className='text-sm text-gray-500'>
            Use placeholders to personalize your invitation messages. Click on a
            placeholder below to insert it into your template.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-semibold'>
              Available Placeholders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
              {PLACEHOLDERS.map((placeholder) => (
                <button
                  key={placeholder.key}
                  onClick={() => handleInsertPlaceholder(placeholder.key)}
                  className='text-left p-2 rounded border hover:bg-gray-50 transition-colors'
                >
                  <code className='text-sm font-mono text-blue-600'>
                    {placeholder.key}
                  </code>
                  <p className='text-xs text-gray-600 mt-1'>
                    {placeholder.description}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className='text-sm font-semibold'>Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='bg-gray-50 p-4 rounded border min-h-[200px]'>
            <pre className='whitespace-pre-wrap text-sm font-sans'>
              {preview || 'Preview will appear here...'}
            </pre>
          </div>
          <p className='text-xs text-gray-500 mt-2'>
            This preview shows how your template will look with sample data.
          </p>
        </CardContent>
      </Card>

      <div className='flex justify-end'>
        <Button
          onClick={handleSave}
          disabled={saving}
          className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  )
}



